import { useCallback, useRef, useState } from 'react';

import type {
	PostSubscriptionsStripeCheckoutSessionRequestBody,
	PostSubscriptionsStripeCheckoutSessionResponseBody,
} from '@calm-web/api-sdk/lib/app-api';

import usePurchaseBodyArgs from '@/components/Sisu/hooks/usePurchaseBodyArgs';
import { useApi } from '@/hooks/api';
import { useUserState } from '@/hooks/store';

import { CHECKOUT_RETURN_URL } from './types';

export type UseCreateCheckoutSessionProps = {
	purchaseArgs?: Record<string, unknown>;
};

/**
 * `createCheckoutSession()`'s resolved value. Callers need the ORIGINATING signature
 * alongside the session: coalescing (see `fireCreate`/`observeSettle` below) can settle a
 * caller's promise with a session that belongs to a LATER selection than the one in flight
 * when that caller called — so the signature closed over at call time is not reliably the
 * signature of what actually comes back. Carrying it in the resolved value lets a caller
 * commit to whatever selection the payload actually corresponds to.
 */
export type CheckoutSessionCreateResult = {
	signature: string;
	session: PostSubscriptionsStripeCheckoutSessionResponseBody;
};

/**
 * The content signature that both dedupes session creates (here) AND keys the provider
 * remount (in `CheckoutSessionsForm`). Sharing one formula keeps the two from drifting —
 * if a create fires for a new signature, the provider must remount so the mounted Elements
 * never confirm a stale session.
 */
export const buildCheckoutSessionSignature = (
	args: Pick<
		// The body is a union of purchase and card-update sessions, and only a purchase has a plan.
		Extract<PostSubscriptionsStripeCheckoutSessionRequestBody, { plan: string }>,
		'plan' | 'coupon' | 'promotion' | 'freeTrial' | 'is30DayOverride'
	>,
): string =>
	JSON.stringify({
		plan: args.plan,
		coupon: args.coupon,
		promotion: args.promotion,
		freeTrial: args.freeTrial,
		// `is30DayOverride` changes the trial LENGTH (30d vs 14d) the backend bakes into the
		// session, so it must key the session like the other identifiers — otherwise a buyer on
		// `?is30DayOverride=true` reuses a 14-day session behind a 30-day UI (trial-length/billing
		// drift). Matches the legacy flow, which forwards it via `...purchaseParams`.
		is30DayOverride: args.is30DayOverride,
	});

/**
 * Creates a Stripe Checkout Session for the embedded Elements surface and returns a
 * `CheckoutSessionCreateResult` (the session, notably its `clientSecret`, alongside the
 * signature it was actually created for — see that type's own doc comment for why). The
 * provider passes `session.clientSecret` directly to `CheckoutElementsProvider`
 * (react-stripe-js takes a clientSecret promise, not a `fetchClientSecret` callback).
 * Plan / coupon / promotion / freeTrial are Calm-level identifiers resolved to Stripe
 * prices server-side.
 *
 * Deduped by a content signature (plan/coupon/promotion/freeTrial): repeated
 * calls for the same inputs return the in-flight (or last) promise instead of
 * POSTing again. This keeps a single `/signup-flow` landing to ONE create even
 * though the provider subtree remounts (its `key` flaps as the plan settles
 * during hydration) and even under StrictMode's mount→cleanup→remount (the create
 * now runs in the provider's effect). A genuine signature change → a new create —
 * but if one is already in flight, that create is *coalesced* rather than fired
 * immediately (see `createCheckoutSession`): the backend allows only one open
 * session-create per user at a time, so two overlapping POSTs trip its idempotency
 * latch and 503. The cache/in-flight state lives in this hook (called from the
 * un-keyed outer form), so it survives remounts.
 *
 * Uses the app's `apiRequest` (PerimeterX / auth / recaptcha aware) with the
 * regenerated app-api request/response types — matching how `useGetClientSecret`
 * consumes the SDK. Reached only when the caller's eligibility conjunction and
 * allowlist enroll the flow into `www-checkout-sessions` (see
 * `PaymentElementsFormRefresh`); the experiment itself is at 100% treatment.
 */
export default function useCreateCheckoutSession({ purchaseArgs }: UseCreateCheckoutSessionProps = {}) {
	const apiRequest = useApi();
	const purchaseBodyArgs = usePurchaseBodyArgs({ purchaseArgs });
	const { plan, coupon, promotion, freeTrial, is30DayOverride } = purchaseBodyArgs;
	const user = useUserState();
	// On a successful purchase, `setUser({...user, subscription})` flips the derived
	// `freeTrial` (a valid sub makes `userCanTrial` false), which churns the signature →
	// the provider remounts / this callback's identity changes → a spurious `create` POST
	// the backend just rejects with `already_subscribed`. Suppress create entirely once a
	// purchase has actually completed THROUGH THIS HOOK INSTANCE.
	//
	// Deliberately a mount-to-now TRANSITION of `.valid` (false → true), snapshotted the same way
	// `CheckoutSessionsForm`'s own `wasValidAtMount`/`hasCompletedPurchaseThisMount` pair is — NOT
	// a live `!!user?.subscription?.valid` recomputed on every call. A family purchase's primary
	// audience is an existing valid individual/premium subscriber upgrading to family (see
	// familyPlan/SignupScreens/index.tsx's determineNextScreenAfterAccount): that buyer is already
	// valid at mount, so a live check would block their very first call through this hook. Mount-
	// snapshotting also protects a LATER call through this SAME hook instance: this hook is one
	// instance per mount (created in the outer, un-keyed `CheckoutSessionsForm`), not one per plan,
	// so a `PlanSkuToggle` selection can change `plan` on a later call without a new hook instance
	// ever being created. A live check re-derived from the CURRENT plan would then see that
	// already-valid buyer's unrelated subscription reassert itself the moment they toggle away
	// from family, permanently blocking every subsequent call through this instance regardless of
	// what it's actually for. A buyer with NO subscription at mount never has this problem — for
	// them the transition fires exactly once, right after their own purchase actually completes,
	// exactly as intended.
	const wasValidAtMountRef = useRef(!!user?.subscription?.valid);
	const hasActiveSubscription = !wasValidAtMountRef.current && !!user?.subscription?.valid;

	// Stable per content (not object identity), so the callback identity below
	// only changes when the actual create inputs change.
	const signature = buildCheckoutSessionSignature({ plan, coupon, promotion, freeTrial, is30DayOverride });

	const cacheRef = useRef<{
		signature: string;
		promise: Promise<CheckoutSessionCreateResult>;
	} | null>(null);
	// True for the lifetime of one `fireCreate` POST (including its internal 503 retry) — a
	// ref (not state) because it must be read/written synchronously inside `createCheckoutSession`,
	// which can run between renders (an effect calling it twice in the same tick).
	const isPendingRef = useRef(false);
	// The most recent selection requested while a *different* create was in flight. Its own
	// request body is snapshotted at call time (not re-derived from the signature later) because
	// by the time it actually fires, `plan`/`coupon`/etc. in this closure may already have moved
	// on to a further selection.
	const queuedRef = useRef<{
		signature: string;
		body: PostSubscriptionsStripeCheckoutSessionRequestBody;
	} | null>(null);
	// The promise handed back to every caller coalesced behind `queuedRef` — there is at most one
	// at a time (a newer coalesced call overwrites `queuedRef`'s target but reuses this same
	// deferred, since only the final selection's result matters to whoever is still listening).
	const deferredRef = useRef<{
		promise: Promise<CheckoutSessionCreateResult>;
		resolve: (value: CheckoutSessionCreateResult) => void;
		reject: (reason?: unknown) => void;
	} | null>(null);
	// Mirrors "a create is in flight, or one is queued behind it" as render-visible state — the
	// refs above can't drive UI (disabling submit/express-checkout) since writing them doesn't
	// trigger a re-render.
	const [isCreating, setIsCreating] = useState(false);
	// A settle handler that runs later (async) needs the CURRENT `hasActiveSubscription`, not
	// whichever render's closure it happened to run in — a plain `const` would read whatever
	// value was true when that particular create started, which can be stale by the time it
	// settles.
	const hasActiveSubscriptionRef = useRef(hasActiveSubscription);
	hasActiveSubscriptionRef.current = hasActiveSubscription;

	// Drop the dedupe cache so the NEXT call re-creates a fresh session. "Try again" calls this
	// before bumping its remount key: a session can RESOLVE with a valid clientSecret yet still be
	// unusable (Stripe Elements init fails — expired/cancelled session, transient read error), and
	// since the cache only self-evicts on promise *rejection*, that dead-but-resolved session would
	// otherwise replay forever (retryAttempt isn't in the signature, by design — that preserves the
	// cache-survives-remount behavior for the hydration plan-key-flap case). Event-time only.
	const evict = useCallback(() => {
		cacheRef.current = null;
	}, []);

	// Issues the actual POST (with the existing retry-once-on-503) for `signature`/`body`. Called
	// either directly (nothing else in flight) or from `observeSettle` below, for a selection
	// that was coalesced while a prior create was still pending. A plain (hoisted) function, not
	// a `useCallback`: it mutually recurses with `observeSettle` by name, and a `const` arrow
	// assigned from `useCallback` can't safely self-reference before the assignment completes.
	// Neither needs referential stability across renders — nothing outside
	// `createCheckoutSession` (itself re-created on every genuine input change) holds onto them.
	function fireCreate(
		createSignature: string,
		body: PostSubscriptionsStripeCheckoutSessionRequestBody,
	): Promise<CheckoutSessionCreateResult> {
		isPendingRef.current = true;
		setIsCreating(true);

		const requestCheckoutSession = () =>
			apiRequest<PostSubscriptionsStripeCheckoutSessionResponseBody>({
				service: 'api',
				endpoint: 'subscriptions/stripe/checkout-session',
				method: 'POST',
				body,
			});

		// This is the promise dedupe callers (`cacheRef`) and awaiters get back — its
		// resolve/reject must reach them exactly as Stripe/the backend produced them.
		// `observeSettle` below watches it purely for the side effects (cache eviction, firing a
		// queued follow-up); it never wraps or replaces it.
		const promise = (async (): Promise<CheckoutSessionCreateResult> => {
			let data: PostSubscriptionsStripeCheckoutSessionResponseBody;
			try {
				data = (await requestCheckoutSession()).data;
			} catch (err) {
				// The create endpoint fails closed with a retryable 503 or 425 when a prior
				// open session can't be transiently expired. Retry once — `apiRequest`'s
				// interceptor flattens `error.response`, so the status is
				// on the top level of the rejected value.
				const status = (err as { status?: number } | undefined)?.status;
				if (status !== 503 && status !== 425) {
					throw err;
				}
				data = (await requestCheckoutSession()).data;
			}
			if (!data?.clientSecret) {
				// `surface: 'elements'` always returns a clientSecret; a null/missing one is an
				// unexpected backend response. THROW (don't resolve) so `observeSettle` evicts the
				// dedupe cache and "Try again" can re-create — otherwise the cache keeps replaying
				// this dead, unusable session forever. (I7) `data` itself can be missing (not just
				// `.clientSecret`): the shared axios interceptor resolves — rather than rejects — a
				// raw network failure (no `.response`, so its `if (httpStatus >= 400)` check never
				// matches), handing back an Error-shaped, non-response value with no `.data` at all.
				// A bare `data.clientSecret` would throw a raw, uncontrolled TypeError instead of
				// this clean, catchable message.
				throw new Error('Checkout Session did not return a client secret');
			}
			// Carry the signature this create actually fired for — a caller coalesced behind
			// this one (see `observeSettle`/`queuedRef` below) must be able to tell which
			// selection the session it just received actually belongs to.
			return { signature: createSignature, session: data };
		})();

		cacheRef.current = { signature: createSignature, promise };
		// Fire-and-forget: `observeSettle` never re-throws (its own `catch` only evicts the
		// cache), so this never rejects — the `.catch` is defensive, not load-bearing.
		observeSettle(promise).catch(() => {});

		return promise;
	}

	// Watches `promise` purely for side effects once it settles — never awaited by a real
	// caller, and never alters what `promise` itself resolves/rejects with. Using `try/finally`
	// here (rather than attaching `.finally()` to `promise` directly) avoids that combinator's
	// own footgun: a `.finally(fn)` call returns a NEW promise that re-throws the original
	// rejection, so forgetting to also `.catch()` that derived promise turns one rejection into a
	// second, unhandled one. `await`ing inside `try/finally` has no such derived promise.
	async function observeSettle(promise: Promise<CheckoutSessionCreateResult>) {
		try {
			await promise;
		} catch {
			// Drop the cache entry on failure so a later attempt (e.g. "Try again") can
			// re-create rather than replaying a rejected promise. Swallowed here — real callers
			// await `promise` itself for the actual failure.
			if (cacheRef.current?.promise === promise) {
				cacheRef.current = null;
			}
		} finally {
			// Release the in-flight slot on settle — resolve OR reject. A create that failed
			// still owes a queued caller a real attempt at the selection they actually asked for
			// (retrying the SAME stale selection would be wrong), so this runs regardless of
			// outcome.
			isPendingRef.current = false;
			const queued = queuedRef.current;
			// Clear before firing the next create: that create's own callers (or a caller that
			// settles back on the in-flight selection) must start from an empty queue, not this
			// one's leftovers.
			queuedRef.current = null;
			const deferred = deferredRef.current;
			deferredRef.current = null;
			if (queued && !hasActiveSubscriptionRef.current) {
				const nextPromise = fireCreate(queued.signature, queued.body);
				if (deferred) {
					nextPromise.then(deferred.resolve, deferred.reject);
				}
			} else {
				// Either nothing was queued, or the buyer subscribed while it was waiting —
				// firing it now would just POST into the same `already_subscribed` rejection the
				// top-level guard above exists to prevent. Drop it: its deferred is left pending,
				// same as that guard's own fallback for this buyer.
				setIsCreating(false);
			}
		}
	}

	const createCheckoutSession = useCallback((): Promise<CheckoutSessionCreateResult> => {
		// Already subscribed (e.g. the post-success remount) → do NOT POST another create the
		// backend would reject with `already_subscribed`. Reuse the cached (just-succeeded)
		// session promise if present; otherwise return a never-resolving promise so the
		// transient provider shows its Loader and unmounts into Handoff. No money-endpoint POST.
		// (The caller MUST NOT treat a never-resolving promise here as "still loading" once it's
		// already showing committed content — `CheckoutSessionsForm`'s own background pre-fetch
		// checks its own equivalent, mount-snapshotted `hasCompletedPurchaseThisMount` before ever
		// calling in here, precisely so a post-purchase signature churn can't end up waiting on a
		// promise that never settles.)
		if (hasActiveSubscription) {
			return cacheRef.current?.promise ?? new Promise<CheckoutSessionCreateResult>(() => {});
		}

		// Same inputs as the current in-flight/last create → reuse it (no extra POST). Settling
		// back on this selection also cancels any different selection queued behind it — the
		// buyer no longer wants that detour. Contract note for any future caller of this hook:
		// whoever was holding onto `deferredRef.current.promise` for that cancelled detour is
		// left with a promise that never settles — it's neither resolved nor rejected here, only
		// dropped. Every CURRENT caller tolerates this because it independently guards itself (the
		// outer form's background-create effect cleans up via its own `ignore` flag before it
		// could ever be waiting on this specific promise), but that's an assumption this hook
		// doesn't enforce — a caller that awaits the returned promise directly without an
		// equivalent guard would hang forever on a cancelled detour.
		if (cacheRef.current?.signature === signature) {
			queuedRef.current = null;
			deferredRef.current = null;
			return cacheRef.current.promise;
		}

		const body: PostSubscriptionsStripeCheckoutSessionRequestBody = {
			plan,
			coupon,
			promotion,
			freeTrial,
			// Trial-length parity with the legacy flow: a 30-day-override buyer must get a 30-day
			// session, not the default 14-day one.
			is30DayOverride,
			surface: 'elements',
			// Carries the `{CHECKOUT_SESSION_ID}` template so Stripe redirects redirect-required
			// methods (Klarna et al.) back to `/checkout/return?session_id=cs_…` — without it they
			// landed on a dead `missing-session` after paying. See CHECKOUT_RETURN_URL.
			returnUrl: CHECKOUT_RETURN_URL,
		};

		if (isPendingRef.current) {
			// A create for a different selection is already in flight. The backend keeps at most
			// one open create per user, so firing this one now would race the in-flight one and
			// 503 the loser — queue it instead. A later call before this settles overwrites the
			// queued selection (not stacks it): only the final one needs its own POST.
			queuedRef.current = { signature, body };
			if (!deferredRef.current) {
				deferredRef.current = Promise.withResolvers<CheckoutSessionCreateResult>();
			}
			return deferredRef.current.promise;
		}

		return fireCreate(signature, body);
		// `fireCreate` itself is excluded: it's a plain function (see its own comment for why
		// it isn't a `useCallback`), so listing it would churn this identity on every render
		// for no reason — the other deps already churn it whenever anything meaningful
		// changes. `apiRequest` IS listed even though `fireCreate` (not this callback body)
		// is what actually calls it — `useApi()` documents its own identity changing on
		// login/consent state independent of these other deps, and dropping it here would
		// let this callback go stale relative to that.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [apiRequest, signature, plan, coupon, promotion, freeTrial, is30DayOverride, hasActiveSubscription]);

	return { createCheckoutSession, evict, isCreating };
}
